import { useCheckRole } from '@ospace/auth'
import { userTypes } from '@ospace/core-auth'
import { PageLink, PageTitle } from '@ospace/layout'
import { ConfirmationModal, ErrorBoundary, showNotification } from '@ospace/shared'
import { Anchor, Badge, Button, Group, Loader, Tabs, Text } from '@mantine/core'
import { IconPlayerStop } from '@tabler/icons-react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { RunEventsLog } from '../components/RunEventsLog'
import { RunGroupsTable } from '../components/RunGroupsTable'
import { RunSummaryCard } from '../components/RunSummaryCard'
import { useCancelDeduplicationRun } from '../hooks/useCancelDeduplicationRun'
import { useDeduplicationKindConfig } from '../hooks/useDeduplicationKind'
import { useDeduplicationRun } from '../hooks/useDeduplicationRun'
import { runDetailPath } from '../utils/paths'

const RunDetailPage = () => {
  const kindConfig = useDeduplicationKindConfig()
  const { runId = '' } = useParams()
  const navigate = useNavigate()
  const { search } = useLocation()
  const tab = new URLSearchParams(search).get('tab') ?? 'groups'
  const { data: run, isLoading, status } = useDeduplicationRun(runId)
  const cancelRun = useCancelDeduplicationRun(runId)

  const breadcrumbs: PageLink[] = [
    { title: kindConfig.title, path: kindConfig.basePath, isActive: false },
    {
      title: `Run ${runId.slice(0, 8)}…`,
      path: runDetailPath(kindConfig.kind, runId),
      isActive: true,
    },
  ]

  const setTab = (value: string | null) => {
    const next = new URLSearchParams(search)
    if (value && value !== 'groups') {
      next.set('tab', value)
    } else {
      next.delete('tab')
    }
    const qs = next.toString()
    navigate(
      { pathname: runDetailPath(kindConfig.kind, runId).split('?')[0], search: qs },
      { replace: true }
    )
  }

  if (isLoading) {
    return (
      <Group justify='center' py='xl'>
        <Loader />
      </Group>
    )
  }

  if (status === 'error' || !run) {
    return <Text c='red'>Run not found.</Text>
  }

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>Run detail</PageTitle>

      <Group justify='space-between' mb='md'>
        <Anchor component={Link} to={`${kindConfig.basePath}?tab=runs`} display='inline-block'>
          ← Back to runs
        </Anchor>

        {kindConfig.manualRunsEnabled && run.status === 'running' && (
          <ConfirmationModal
            alertMessage={
              <Text size='sm'>
                This stops the coordinator task on Fargate and marks the run as cancelled in
                observability. Processing may take a few seconds to wind down.
              </Text>
            }
            isLoading={cancelRun.isLoading}
            action={async () => {
              try {
                const result = await cancelRun.mutateAsync()
                showNotification({
                  title: 'Run cancelled',
                  message: result.message,
                  type: 'success',
                })
              } catch (error) {
                showNotification({
                  title: 'Failed to cancel run',
                  message: error instanceof Error ? error.message : 'Unknown error',
                  type: 'error',
                })
              }
            }}
            renderButton={({ onClick }) => (
              <Button
                color='red'
                variant='light'
                leftSection={<IconPlayerStop size={16} />}
                onClick={onClick}
              >
                Cancel run
              </Button>
            )}
          />
        )}
      </Group>

      <RunSummaryCard kind={kindConfig.kind} run={run} />

      <Tabs value={tab} onChange={setTab} mt='lg'>
        <Tabs.List>
          <Tabs.Tab value='groups'>Groups</Tabs.Tab>
          <Tabs.Tab value='log'>Event log</Tabs.Tab>
          <Tabs.Tab value='overview'>Overview</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value='groups' pt='md'>
          <RunGroupsTable kind={kindConfig.kind} runId={runId} />
        </Tabs.Panel>

        <Tabs.Panel value='log' pt='md' keepMounted>
          <RunEventsLog runId={runId} />
        </Tabs.Panel>

        <Tabs.Panel value='overview' pt='md'>
          <Group gap='xs' mb='md'>
            {Object.entries(run.terminals ?? {}).map(([terminal, count]) => (
              <Badge key={terminal} variant='light' size='lg'>
                {terminal}: {count}
              </Badge>
            ))}
          </Group>
          <Text c='dimmed' size='sm'>
            Groups: {run.groupsProcessed}
            {run.groupsTotal != null ? ` / ${run.groupsTotal}` : ''} · {kindConfig.entityCountLabel}
            : {run.companiesTotal ?? '—'} · {kindConfig.reducedLabel}: {run.companiesReduced ?? 0} ·
            Failed groups: {run.groupsFailed ?? run.statsJson?.groupsFailed ?? 0}
          </Text>
        </Tabs.Panel>
      </Tabs>
    </>
  )
}

const Controller = () => {
  useCheckRole([userTypes.Admin])
  return <RunDetailPage />
}

export const RunDetailPageRouteHandler = () => (
  <ErrorBoundary>
    <Controller />
  </ErrorBoundary>
)
